(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FILL_EASE = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';

  class SaResearchCollab extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.slides = [];
      this._timer = null;
      this._paused = { hover: false, focus: false, hidden: false, offscreen: false, editing: false };
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onEnter = this._onEnter.bind(this);
      this._onLeave = this._onLeave.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onBlockDeselect = this._onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-rc-viewport]');
      this.track = this.querySelector('[data-rc-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-rc-slide]'));
      this.prevBtn = this.querySelector('[data-rc-prev]');
      this.nextBtn = this.querySelector('[data-rc-next]');
      this.progress = this.querySelector('[data-rc-progress]');
      this.counterCurrent = this.querySelector('[data-rc-current]');
      if (!this.track || this.slides.length === 0) return;

      this.total = this.slides.length;
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      // Autoplay also runs in the theme editor so merchants can preview it; while a
      // slide/block is actively selected for editing it pauses (see block select/deselect).
      this.autoplay = this.hasAttribute('data-rc-autoplay') && this.total > 1 && !REDUCE;
      this.autoplaySpeed = parseInt(this.dataset.rcAutoplaySpeed, 10) || 6000;
      // Loop follows its own setting only. With loop off + autoplay on, it advances to
      // the last slide and stops (arrows disable at the ends), instead of wrapping.
      this.loop = this.hasAttribute('data-rc-loop');

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.addEventListener('keydown', this._onKeydown);
      if (this.viewport && window.PointerEvent) {
        this.viewport.addEventListener('pointerdown', this._onPointerDown);
      }

      if (this.autoplay) {
        this.addEventListener('pointerenter', this._onEnter);
        this.addEventListener('pointerleave', this._onLeave);
        this.addEventListener('focusin', this._onFocusIn);
        this.addEventListener('focusout', this._onFocusOut);
        document.addEventListener('visibilitychange', this._onVisibility);
        if ('IntersectionObserver' in window) {
          this._io = new IntersectionObserver(
            (entries) => {
              const e = entries[0];
              this._paused.offscreen = !e.isIntersecting;
              this._reconcilePlayback();
            },
            { threshold: 0.25 }
          );
          this._io.observe(this);
        }
      }

      if (this.designMode) {
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      }

      this._setFill(0, 'none');
      if (this.progress) void this.progress.offsetWidth;
      this.setActive(0);
    }

    disconnectedCallback() {
      this._clearTimer();
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      this.removeEventListener('keydown', this._onKeydown);
      if (this.viewport) this.viewport.removeEventListener('pointerdown', this._onPointerDown);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      this.removeEventListener('pointerenter', this._onEnter);
      this.removeEventListener('pointerleave', this._onLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._io) this._io.disconnect();
      if (this.designMode) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      }
    }

    setActive(i, opts) {
      opts = opts || {};
      let next = i;
      if (this.loop) next = ((i % this.total) + this.total) % this.total;
      else next = Math.max(0, Math.min(i, this.total - 1));
      this.index = next;

      this.slides.forEach((slide, idx) => slide.classList.toggle('is-active', idx === next));

      this._syncUI();
      this._updateProgress(opts.animate !== false);
      this._scheduleNext();
    }

    next(manual) {
      this.setActive(this.index + 1);
      if (manual) {
      }
    }

    prev(manual) {
      this.setActive(this.index - 1);
      if (manual) {
      }
    }

    _updateProgress(animate) {
      const p = (this.index + 1) / this.total;
      this._setFill(p, animate && !REDUCE ? FILL_EASE : 'none');
    }

    _setFill(scale, transition) {
      if (!this.progress) return;
      this.progress.style.transition = transition;
      this.progress.style.transform = 'scaleX(' + scale.toFixed(4) + ')';
    }

    _scheduleNext() {
      this._clearTimer();
      if (!this.autoplay || this._isPaused()) return;
      // When not looping, stop auto-advancing once the last slide is reached.
      if (!this.loop && this.index >= this.total - 1) return;
      this._timer = window.setTimeout(() => {
        this._timer = null;
        this.setActive(this.index + 1);
      }, this.autoplaySpeed);
    }

    _clearTimer() {
      if (this._timer) {
        window.clearTimeout(this._timer);
        this._timer = null;
      }
    }

    _isPaused() {
      return (
        this._paused.hover ||
        this._paused.focus ||
        this._paused.hidden ||
        this._paused.offscreen ||
        this._paused.editing
      );
    }

    _reconcilePlayback() {
      if (!this.autoplay) return;
      if (this._isPaused()) this._clearTimer();
      else if (!this._timer) this._scheduleNext();
    }

    _syncUI() {
      if (this.counterCurrent) this.counterCurrent.textContent = String(this.index + 1);
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === this.total - 1;
      }
      this.slides.forEach((slide, i) => {
        const hidden = i !== this.index;
        slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        slide.querySelectorAll('a, button').forEach((el) => {
          if (hidden) el.setAttribute('tabindex', '-1');
          else el.removeAttribute('tabindex');
        });
      });
    }

    _onPrev(e) {
      e.preventDefault();
      this.prev(true);
    }

    _onNext(e) {
      e.preventDefault();
      this.next(true);
    }

    _onKeydown(e) {
      if (e.target.closest('a, button, input, textarea, select')) {
        if (!e.target.closest('[data-rc-prev], [data-rc-next]')) return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next(true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev(true);
      }
    }

    _onEnter() {
      this._paused.hover = true;
      this._reconcilePlayback();
    }

    _onLeave() {
      this._paused.hover = false;
      this._reconcilePlayback();
    }

    _onFocusIn() {
      this._paused.focus = true;
      this._reconcilePlayback();
    }

    _onFocusOut() {
      if (this.contains(document.activeElement)) return;
      this._paused.focus = false;
      this._reconcilePlayback();
    }

    _onVisibility() {
      this._paused.hidden = document.hidden;
      this._reconcilePlayback();
    }

    _onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this._dragStartX = e.clientX;
      this._dragStartY = e.clientY;
      this._dragging = false;
      window.addEventListener('pointermove', this._onPointerMove, { passive: false });
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(e) {
      if (this._dragStartX == null) return;
      const dx = e.clientX - this._dragStartX;
      const dy = e.clientY - this._dragStartY;
      if (!this._dragging) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        this._dragging = true;
        this.classList.add('is-dragging');
      }
      e.preventDefault();
      this._dragDx = dx;
    }

    _onPointerUp() {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
      const dx = this._dragDx || 0;
      const wasDragging = this._dragging;
      this._dragStartX = null;
      this._dragStartY = null;
      this._dragDx = 0;
      this._dragging = false;
      this.classList.remove('is-dragging');
      if (!wasDragging) return;
      const threshold = Math.min(120, (this.viewport.clientWidth || 320) * 0.15);
      if (dx <= -threshold) this.next(true);
      else if (dx >= threshold) this.prev(true);
    }

    _onBlockSelect(e) {
      const slide = e.target && e.target.closest && e.target.closest('[data-rc-slide]');
      if (!slide) return;
      const idx = this.slides.indexOf(slide);
      // Pause autoplay on the slide being edited so it doesn't advance away.
      this._paused.editing = true;
      if (idx > -1) this.setActive(idx);
      this._reconcilePlayback();
    }

    _onBlockDeselect() {
      this._paused.editing = false;
      this._reconcilePlayback();
    }
  }

  if (!customElements.get('sa-research-collab')) {
    customElements.define('sa-research-collab', SaResearchCollab);
  }
})();
